const { introspectionApi } = require('da-graphql-client/web');
const { capitalCase, sentenceCase } = require('change-case');
const iso3166 = require('iso-3166-2');
const { QueryBuilderAttributeTypes } = require('./operators');
const { attributePathToString } = require('./helper');
const { GraphQLFieldKinds } = introspectionApi;
const { isConsolidatedAttribute, consolidateRuleAttributes } = require('./consolidate');
const { countries } = require('./country');


/**
 * The list of enum field paths that require ISO code subdivision translation.
 */
const attributePathsForSubdivisionISO = [
    ...countries.map((c) => `${c}.region.code`),
    ...countries.map((c) => `${c}.subRegion.code`),
];

/**
 * The list of enum field paths that require ISO code country translation.
 */
const attributePathsForCountryISO = [
    'place.country',
    'identityDocuments.country',
    'citizenshipCountry',
    'countryOfBirth',
    'country.generic.code'
];

/**
 * Return true if the ethos type is an enum; false otherwise.
 */
const isEnumType = (ethosType, fieldsProperty = 'inputFields') => {
    return ethosType[fieldsProperty]
        && ethosType[fieldsProperty].length
        && ethosType[fieldsProperty][0].type
        && ethosType[fieldsProperty][0].type.kind === GraphQLFieldKinds.Enum;
};

/**
 * Utility to identify attributes in the rules that have empty enum attributes.
 */
const identifyMissingAttributeEnums = (where, attributes) => {
    if (!where || !where.length) {
        return { attributesToPopulate: [], graphQLTypeNames: [] };
    }

    const attributesToPopulate = [];
    const graphQLTypeNames = new Set();

    consolidateRuleAttributes(where, attributes);

    // grab the unpopulated enum attributes
    where.forEach((rule) => {
        if (!rule.attribute) {
            return;
        }

        const attribute = rule.attribute;

        if (!attribute) {
            return;
        }

        // look for enum attributes having no specified enum values
        if (attribute.type === QueryBuilderAttributeTypes.Enum
            && (!attribute.enum || !attribute.enum.length)) {
            attributesToPopulate.push(attribute);
        }
    });

    if (attributesToPopulate.length) {
        // match the paths represented in attributesToPopulate with
        // paths represented in attributes and pull the graphQLTypeNames
        // from there instead of using stored graphQLTypeNames;
        const attributesByPath = new Map();
        attributes.forEach((a) => attributesByPath.set(attributePathToString(a), a));

        attributesToPopulate.forEach((a) => {
            const attributePath = attributePathToString(a);
            const attribute = attributesByPath.get(attributePath);

            if (isConsolidatedAttribute(attribute)) {
                for (const nestedAttribute of attribute.consolidatedAttributes) {
                    graphQLTypeNames.add(nestedAttribute.graphQLTypeName);
                }
            } else {
                const graphQLTypeName = attribute ? attribute.graphQLTypeName : a.graphQLTypeName;
                if (graphQLTypeName) {
                    graphQLTypeNames.add(graphQLTypeName);
                    a.graphQLTypeName = graphQLTypeName;
                }
            }
        });
    }

    return { attributesToPopulate, graphQLTypeNames: Array.from(graphQLTypeNames) };
};

/**
 * Utility to populate given attribute enums from given graphQL types.
 */
const setAttributeEnums = (attributesToPopulate, graphTypes) => {
    if (!attributesToPopulate || !attributesToPopulate.length || !graphTypes || !graphTypes.length) {
        return;
    }

    attributesToPopulate.forEach((a) => {
        if (isConsolidatedAttribute(a)) {
            for (const nestedAttribute of a.consolidatedAttributes) {
                const eT = graphTypes.find((e) => e.name === nestedAttribute.graphQLTypeName);
                if (eT && eT.enumValues) {
                    nestedAttribute.enum = getEnums(a, eT.enumValues);
                    a.enum = [...a.enum || [], ...nestedAttribute.enum];
                }
            }
            if (a.enum) {
                a.enum.sort(sortByLabelExpression);
            }
        } else {
            const ethosType = graphTypes.find((e) => e.name === a.graphQLTypeName);
            if (!ethosType || !ethosType.enumValues) {
                a.enum = [];
                return;
            }

            a.enum = getEnums(a, ethosType.enumValues);
        }
    });
};

/**
 * Get the enum label which may require an ISO translation.  The default is to
 * translate as 'myEnum' to 'My Enum'.
 */
const getEnums = (attribute, enumValues) => {
    let doIsoSubdivision = false;
    let doIsoCountry = false;
    const attributePath = attributePathToString(attribute);

    const translateIsoCode = (isoCode, translator = (code) => { name }) => {
        if (!isoCode) {
            return isoCode;
        }

        const refinedIsoCode = isoCode.replace('_', '-');

        const result = translator(refinedIsoCode);
        if (result) {
            return result.name;
        }

        return refinedIsoCode;
    };

    for (const isoPath of attributePathsForSubdivisionISO) {
        if (attributePath.endsWith(isoPath)) {
            doIsoSubdivision = true;
            break;
        }
    }

    if (!doIsoSubdivision) {
        for (const isoPath of attributePathsForCountryISO) {
            if (attributePath.endsWith(isoPath)) {
                doIsoCountry = true;
                break;
            }
        }
    }

    const enums = (enumValues || []).map((v) => {
        let label = '';

        if (doIsoSubdivision) {
            label = translateIsoCode(v.name, iso3166.subdivision);
        } else if (doIsoCountry) {
            label = translateIsoCode(v.name, iso3166.country);
        }

        return {
            label: label || capitalCase(sentenceCase(v.name)),
            value: v.name
        };
    });

    // sort the enums by label
    enums.sort(sortByLabelExpression);

    return enums;
};

const sortByLabelExpression = (a, b) => {
    const aLabel = a.label ? a.label.toLocaleUpperCase() : a.label;
    const bLabel = b.label ? b.label.toLocaleUpperCase() : b.label;
    return aLabel < bLabel ? -1 : aLabel > bLabel ? 1 : 0;
};

module.exports = {
    identifyMissingAttributeEnums,
    isEnumType,
    setAttributeEnums,
    attributePathsForSubdivisionISO,
    attributePathsForCountryISO
};
